import { Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from '@stencil/core'

import { CssClassMap } from '../../../utils/interfaces'
import { TABLE_TOOLTIP_MODE, TABLE_TOOLTIP_STATE } from '../table-constants'
import { TableAlignment, TableOverflow, TableTooltipMode, TableTooltipState } from '../table-types'

/**
 * ## Design System
 *
 * O br-table-cell é um subcomponente da tabela que representa uma célula de conteúdo dentro da tabela, sendo utilizado para definir o conteúdo e as propriedades de formatação das células de dados da tabela.
 * Ele é projetado para ser utilizado dentro do subcomponente br-table-row, que representa uma linha da tabela, permitindo a organização e estruturação do conteúdo das células de dados da tabela.
 *
 * @remarks
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/table?tab=designer).
 *
 * @Slot default: Área para inclusão de conteúdo da célula de dados da tabela.
 */
@Component({
  tag: 'br-table-cell',
  styleUrl: 'table-cell.scss',
  shadow: true,
})
export class TableCell {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el!: HTMLBrTableCellElement

  private tooltipRef: TooltipApi | null = null
  private truncateContentRef: HTMLDivElement | null = null
  private contentSlotRef: HTMLSlotElement | null = null

  @State() private isTooltipVisible: boolean = false
  @State() private tooltipContent: string = ''

  private getFullCellContent() {
    if (this.contentSlotRef === null) {
      return ''
    }

    return this.contentSlotRef
      .assignedNodes({ flatten: true })
      .map((node) => node.textContent?.trim() || '')
      .filter((text) => text.length > 0)
      .join(' ')
  }

  private getColumnHeader(): HTMLElement | null {
    const table = this.el.closest('br-table')

    if (table === null) {
      return null
    }

    const row = this.el.closest('br-table-row')

    if (row === null) {
      return null
    }

    const cells = Array.from(row.querySelectorAll('br-table-cell'))
    const columnIndex = cells.indexOf(this.el)

    if (columnIndex === -1) {
      return null
    }

    const headers = Array.from(table.querySelectorAll('br-table-header-cell'))

    return headers[columnIndex] ?? null
  }

  private resolveTooltipState(): TableTooltipState {
    const cellValue = this.el.getAttribute('tooltip-state') as TableTooltipState | null
    if (cellValue !== null) return cellValue

    const row = this.el.closest('br-table-row')
    const rowValue = row?.getAttribute('tooltip-state') as TableTooltipState | null
    if (rowValue !== null) return rowValue

    const columnHeader = this.getColumnHeader()
    const columnValue = columnHeader?.getAttribute('tooltip-state') as TableTooltipState | null
    if (columnValue !== null) return columnValue

    const table = this.el.closest('br-table')
    const tableValue = table?.getAttribute('tooltip-state') as TableTooltipState | null
    if (tableValue !== null) return tableValue

    return TABLE_TOOLTIP_STATE.INFO
  }

  private resolveTooltipMode(): TableTooltipMode {
    const cellValue = this.el.getAttribute('tooltip-mode') as TableTooltipMode | null
    if (cellValue !== null && cellValue !== TABLE_TOOLTIP_MODE.INHERIT) return cellValue

    const row = this.el.closest('br-table-row')
    const rowValue = row?.getAttribute('tooltip-mode') as TableTooltipMode | null
    if (rowValue !== null && rowValue !== TABLE_TOOLTIP_MODE.INHERIT) return rowValue

    const columnHeader = this.getColumnHeader()
    const columnValue = columnHeader?.getAttribute('tooltip-mode') as TableTooltipMode | null
    if (columnValue !== null && columnValue !== TABLE_TOOLTIP_MODE.INHERIT) return columnValue

    const table = this.el.closest('br-table')
    const tableValue = table?.getAttribute('tooltip-mode') as TableTooltipMode | null
    if (tableValue !== null && tableValue !== TABLE_TOOLTIP_MODE.INHERIT) return tableValue

    return TABLE_TOOLTIP_MODE.DISABLED
  }

  private handleMouseEnter = async () => {
    if (this.resolveTooltipMode() !== TABLE_TOOLTIP_MODE.ENABLED) {
      return
    }

    const truncateContent = this.truncateContentRef

    if (truncateContent === null) {
      return
    }

    this.tooltipContent = this.getFullCellContent()

    if (this.tooltipContent.length === 0) {
      return
    }

    this.isTooltipVisible = true

    await requestAnimationFramePromise()

    if (this.tooltipRef !== null) {
      await this.tooltipRef.setTriggerElement(truncateContent)
      await this.tooltipRef.showTooltip()
    }
  }

  private handleMouseLeave = async () => {
    if (this.tooltipRef !== null) {
      await this.tooltipRef.hideTooltip()
    }

    this.isTooltipVisible = false
  }

  /**
   * Define o alinhamento horizontal do conteúdo dentro da célula.
   */
  @Prop({ reflect: true }) horizontalAlignment?: TableAlignment

  /**
   * Define o alinhamento vertical do conteúdo dentro da célula.
   */
  @Prop({ reflect: true }) verticalAlignment?: TableAlignment

  /**
   * Define se o conteúdo dentro da célula deve ser truncado, permitindo a herança dessa configuração para as células filhas com base no valor definido.
   */
  @Prop({ reflect: true }) overflow?: TableOverflow

  /**
   * Define o estado semântico do tooltip nesta célula.
   */
  @Prop({ reflect: true }) tooltipState?: TableTooltipState

  /**
   * Define o modo de resolução do tooltip nesta célula.
   */
  @Prop({ reflect: true }) tooltipMode?: TableTooltipMode

  /**
   * Evento customizado emitido quando o alinhamento é alterado, permitindo que a célula reaja dinamicamente a mudanças nos tokens de alinhamento.
   */
  @Event({ bubbles: true, composed: true }) brAlignmentChange!: EventEmitter<void>

  /**
   * Evento customizado emitido quando a configuração de overflow é alterada, permitindo que a célula reaja dinamicamente a mudanças no token de overflow.
   */
  @Event({ bubbles: true, composed: true }) brOverflowChange!: EventEmitter<void>

  /**
   * Gatilho do ciclo de vida do componente que é chamado após o componente ser carregado, garantindo que o alinhamento inicial seja aplicado ao conteúdo da célula.
   */
  componentDidLoad() {
    this.notifyAlignmentChange()
    this.notifyOverflowChange()
  }

  /**
   * Observa mudanças nos tokens de alinhamento horizontal e vertical, emitindo um evento para notificar que o alinhamento foi alterado, permitindo que a célula reaja dinamicamente a essas mudanças.
   */
  @Watch('horizontalAlignment')
  @Watch('verticalAlignment')
  protected handleAlignmentChange() {
    this.notifyAlignmentChange()
  }

  /**
   * Observa mudanças no token de overflow, emitindo um evento para notificar que a configuração de overflow foi alterada, permitindo que a célula reaja dinamicamente a essa mudança.
   */
  @Watch('overflow')
  protected handleOverflowChange() {
    this.notifyOverflowChange()
  }

  /**
   * Notifica que o alinhamento foi alterado, emitindo o evento customizado correspondente.
   */
  private notifyAlignmentChange() {
    this.brAlignmentChange.emit()
  }

  /**
   * Notifica que a configuração de overflow foi alterada, emitindo o evento customizado correspondente.
   */
  private notifyOverflowChange() {
    this.brOverflowChange.emit()
  }

  /**
   * Retorna o mapa de classes base do componente.
   *
   * @returns Um objeto representando as classes CSS a serem aplicadas ao elemento da célula da tabela.
   */
  private getCellCssClassMap(): CssClassMap {
    return {
      'br-table-cell': true,
    }
  }

  /**
   * Renderiza o host do componente.
   *
   * @returns JSX.Element representando o elemento host do componente, contendo a estrutura da célula da tabela.
   */
  render() {
    return (
      <Host role="cell">
        <div class={this.getCellCssClassMap()} role="presentation">
          <div class="br-table-cell-content" role="presentation">
            <div
              class="br-table-cell-truncate"
              role="presentation"
              ref={(el) => (this.truncateContentRef = el as HTMLDivElement)}
              onMouseEnter={this.handleMouseEnter}
              onMouseLeave={this.handleMouseLeave}
            >
              <slot ref={(el) => (this.contentSlotRef = el as HTMLSlotElement)}></slot>
            </div>
            {this.isTooltipVisible && (
              <br-tooltip
                ref={(el) => (this.tooltipRef = el as TooltipApi)}
                position="top"
                state={
                  this.resolveTooltipState() === TABLE_TOOLTIP_STATE.DANGER ? 'danger' : this.resolveTooltipState()
                }
              >
                <span slot="content">{this.tooltipContent}</span>
              </br-tooltip>
            )}
          </div>
        </div>
      </Host>
    )
  }
}

type TooltipApi = HTMLBrTooltipElement & {
  setTriggerElement: (element: HTMLElement | null) => Promise<void>
  showTooltip: () => Promise<void>
  hideTooltip: () => Promise<void>
}

const requestAnimationFramePromise = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve())
  })
